import React from 'react';
import { Card, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Calendar, CheckCircle2, Clock, Edit, Instagram, Youtube, Music } from 'lucide-react';
export type ContentType = 'post' | 'story' | 'video' | 'reel';
export type DeliverableStatus = 'planned' | 'in_progress' | 'submitted' | 'approved' | 'published';
export type Platform = 'instagram' | 'tiktok' | 'youtube' | 'facebook' | 'twitter';
export interface Deliverable {
  id: string;
  projectId: string;
  creatorId: string;
  creatorName: string;
  contentType: ContentType;
  platform: Platform;
  status: DeliverableStatus;
  deadline: string;
  description: string;
  notes?: string;
}
interface DeliverableCardProps {
  deliverable: Deliverable;
  onEdit?: (deliverable: Deliverable) => void;
  onStatusChange?: (id: string, status: DeliverableStatus) => void;
}
export function DeliverableCard({
  deliverable,
  onEdit,
  onStatusChange
}: DeliverableCardProps) {
  const statusConfig = {
    planned: {
      variant: 'secondary' as const,
      label: 'Planned',
      color: 'text-gray-600'
    },
    in_progress: {
      variant: 'default' as const,
      label: 'In Progress',
      color: 'text-blue-600'
    },
    submitted: {
      variant: 'warning' as const,
      label: 'Submitted',
      color: 'text-yellow-600'
    },
    approved: {
      variant: 'success' as const,
      label: 'Approved',
      color: 'text-green-600'
    },
    published: {
      variant: 'success' as const,
      label: 'Published',
      color: 'text-green-700'
    }
  };
  const contentTypeLabels = {
    post: 'Post',
    story: 'Story',
    video: 'Video',
    reel: 'Reel'
  };
  const platformIcons = {
    instagram: <Instagram className="h-4 w-4" data-id="element-182" />,
    tiktok: <Music className="h-4 w-4" data-id="element-183" />,
    youtube: <Youtube className="h-4 w-4" data-id="element-184" />,
    facebook: <Instagram className="h-4 w-4" data-id="element-185" />,
    twitter: <Instagram className="h-4 w-4" data-id="element-186" />
  };
  const daysUntilDeadline = Math.ceil((new Date(deliverable.deadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
  const isOverdue = daysUntilDeadline < 0;
  const isUrgent = daysUntilDeadline >= 0 && daysUntilDeadline < 3;
  const currentStatus = statusConfig[deliverable.status];
  return <Card className="hover:shadow-md transition-shadow" data-id="element-187">
      <CardContent className="p-4" data-id="element-188">
        <div className="flex items-start justify-between mb-3" data-id="element-189">
          <div className="flex items-center space-x-2" data-id="element-190">
            <div className="text-gray-400" data-id="element-191">
              {platformIcons[deliverable.platform]}
            </div>
            <div data-id="element-192">
              <h4 className="font-semibold text-sm text-gray-900" data-id="element-193">
                {contentTypeLabels[deliverable.contentType]}
              </h4>
              <p className="text-xs text-gray-500 capitalize" data-id="element-194">
                {deliverable.platform}
              </p>
            </div>
          </div>
          <Badge variant={currentStatus.variant} className="capitalize" data-id="element-195">
            {currentStatus.label}
          </Badge>
        </div>

        <p className="text-sm text-gray-700 mb-3 line-clamp-2" data-id="element-196">
          {deliverable.description}
        </p>

        <div className="flex items-center justify-between text-xs text-gray-500 mb-3" data-id="element-197">
          <div className="flex items-center" data-id="element-198">
            <Calendar className="h-3 w-3 mr-1" data-id="element-199" />
            <span className={isOverdue ? 'text-red-600 font-medium' : isUrgent ? 'text-orange-600 font-medium' : ''} data-id="element-200">
              {deliverable.deadline}
              {isOverdue && ' (Overdue)'}
              {isUrgent && !isOverdue && ` (${daysUntilDeadline}d left)`}
            </span>
          </div>
          <span className="text-gray-600" data-id="element-201">{deliverable.creatorName}</span>
        </div>

        <div className="flex items-center space-x-2" data-id="element-202">
          {deliverable.status !== 'published' && <select value={deliverable.status} onChange={e => onStatusChange?.(deliverable.id, e.target.value as DeliverableStatus)} className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-203">
              <option value="planned" data-id="element-204">Planned</option>
              <option value="in_progress" data-id="element-205">In Progress</option>
              <option value="submitted" data-id="element-206">Submitted</option>
              <option value="approved" data-id="element-207">Approved</option>
              <option value="published" data-id="element-208">Published</option>
            </select>}
          {deliverable.status === 'published' && <div className="flex-1 flex items-center text-xs text-green-600" data-id="element-209">
              <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-210" />
              <span data-id="element-211">Completed</span>
            </div>}
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => onEdit?.(deliverable)} data-id="element-212">
            <Edit className="h-3 w-3" data-id="element-213" />
          </Button>
        </div>
      </CardContent>
    </Card>;
}